import {
  Constant,
  CountOver,
  Desc,
  Eq,
  Relation,
  From,
  RankOver,
  AST,
  Expression,
  And,
  Or,
  Count,
  RowNumberOver,
} from '@cotera/era';
import { SYSTEM_TABLES } from '../../runtime/system-tables';
import { EventStreamDefinition } from '../../manifest';
import { DateTime } from 'luxon';
import { streamSpecifierToEventStreamIds } from '../../builder/stream-specifier-to-event-stream-ids';

export const UncommitedEventStreamEvents = (params: {
  SYSTEM_TABLES?: Partial<
    Record<keyof typeof SYSTEM_TABLES, AST.TableDescription | Relation>
  >;
  eventStream: EventStreamDefinition;
  detectedAt?: Date;
}): Relation => {
  const {
    entity_events = From(SYSTEM_TABLES.entity_events),
    stable_ids = From(SYSTEM_TABLES.stable_ids),
  } = params.SYSTEM_TABLES ?? SYSTEM_TABLES;

  const { eventStream } = params;
  const { specifier, debounceByIdForSecs, throttle, identifier } = eventStream;
  const { entityName, streamId } = streamSpecifierToEventStreamIds(specifier);

  const rel = Relation.fromAst(eventStream.rel);
  const detectedAt = params.detectedAt ?? new Date();
  const LatestEventsForEntity = From(entity_events)
    .where((t) => Eq(t.attr('entity_id'), entityName))
    .select((t) => ({
      ...t.star(),
      rkk: RankOver({
        partitionBy: [t.attr('cotera_stable_id'), t.attr('event_stream_id')],
        orderBy: Desc(t.attr('detected_at_time')),
      }),
    }))
    .where((t) => Eq(t.attr('rkk'), 1));

  const ThrottleEventsForEventStream = From(entity_events)
    .where((t) =>
      And(
        Eq(t.attr('entity_id'), entityName),

        throttle?.frequency === undefined
          ? true
          : t
              .attr('detected_at_time')
              .cast('timestamp')
              .gte(
                Constant(detectedAt)
                  .dateTrunc(throttle.frequency)
                  .cast('timestamp')
              )
      )
    )
    .summary((_t) => ({
      count: Count(),
    }))
    .select((t) => ({
      ...t.star(),
      remaining_events:
        throttle?.maxEvents === undefined
          ? Constant(null, { ty: 'int' })
          : Constant(throttle.maxEvents).sub(t.attr('count').coalesce(0)),
    }));

  const throttleEventsJoin = (x: Relation) => {
    if (throttle === undefined) {
      return x;
    } else {
      return x
        .leftJoin(ThrottleEventsForEventStream, (t, latest) => ({
          on: true,
          select: {
            ...t.star(),
            ...latest.pick('remaining_events'),
            rkk: RowNumberOver({ orderBy: throttle.priorityBy(t) }),
          },
        }))
        .where((t) => t.attr('rkk').lte(t.attr('remaining_events')));
    }
  };

  const TargetWithStableId = rel
    .select((t) => ({
      ...t.star(),
      __identifier: Expression.fromAst(identifier)
        .andThen((x) => (x.implements('string') ? x : x.cast('string')))
        .invariants({
          'Identifier is Unique': CountOver(1, {
            partitionBy: Expression.fromAst(eventStream.identifier),
          }).eq(1),
        }),
    }))
    .innerJoin(
      From(stable_ids).where((t) => t.attr('definition_id').eq(entityName)),
      (target, sids) => ({
        on: Eq(sids.attr('identifier').tag(), target.attr('__identifier')),
        select: {
          ...target.star(),
          ...sids.pick('cotera_stable_id'),
        },
      })
    );

  const NewEvents = TargetWithStableId.select((t) => {
    const value = Expression.fromAst(eventStream.onChange);
    return {
      ...t.star(),
      value: value.implements('string') ? value : value.cast('string'),
    };
  })
    .leftJoin(
      LatestEventsForEntity.where((t) =>
        Eq(t.attr('event_stream_id'), streamId)
      ),
      (target, events) => ({
        on: Eq(
          target.attr('cotera_stable_id'),
          events.attr('cotera_stable_id')
        ),
        select: {
          ...target.star(),
          ...target.pick('cotera_stable_id'),
          // The following two casts look redundant, but they are not.
          // Redshift can't infer the type of the string literal in an insert
          // statement, and so you have to explicitly cast to string.
          entity_id: Constant(entityName, {
            ty: 'string',
          }).cast('string'),
          event_stream_id: Constant(streamId, {
            ty: 'string',
          }).cast('string'),
          detected_at_time: Constant(detectedAt).cast('timestamp'),
          value: target.attr('value'),
          __old_value: events.attr('value'),
          __old_detected_at_time: events.attr('detected_at_time'),
        },
      })
    )
    .where((t) =>
      And(
        t.attr('__old_value').isDistinctFrom(t.attr('value')),
        debounceByIdForSecs === undefined
          ? true
          : Or(
              Constant(
                DateTime.fromJSDate(detectedAt)
                  .minus({ seconds: debounceByIdForSecs })
                  .toJSDate()
              )
                .cast('timestamp')
                .gte(t.attr('__old_detected_at_time').cast('timestamp')),
              t.attr('__old_detected_at_time').isNull()
            )
      )
    )
    .andThen(throttleEventsJoin)
    .select((t) =>
      t.pick(
        'cotera_stable_id',
        'entity_id',
        'event_stream_id',
        'detected_at_time',
        'value'
      )
    );

  return NewEvents;
};
